import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import {
  isClientInstanceRunning,
  isClientInstanceMissing,
  CLIENT_INSTANCE_ROLE_ORDER,
  type ClientInstance,
  type ClientInstanceGroup,
  type ClientInstanceStatus,
  type ClientInstanceStatusFilter,
  type InstanceActionType,
  type InstanceStatusEnvelope,
  type InstanceUpdateEnvelope,
} from "../infrastructure.types";
import { canManageInfra } from "../rbac";
import { runInstanceAction } from "../instanceActions";
import { followHandOff } from "../InstanceActions/InstanceActionsStore";
import { subscribeInstanceStatus, subscribeInstanceUpdates } from "../instanceStreams";
import {
  CLIENT_INSTANCE_EXPORT_COLUMNS,
  toClientInstanceExportRow,
  type ExportRecord,
} from "../infrastructureExports";
import { exportToCsv } from "shared/common/utils/export";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import {
  dateValue,
  nextSort,
  sortRows,
  textValue,
  type SortDir,
  type SortValue,
} from "components/TableSort/tableSort";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";

/** Columns of the per-client instance tables that can be sorted. */
export type ClientInstanceSortKey =
  | "role"
  | "status"
  | "image"
  | "host"
  | "container"
  | "lastSeen";

/** Sort keys that read best newest-first on first click. */
const DESC_FIRST: ReadonlySet<ClientInstanceSortKey> = new Set(["lastSeen"]);

/**
 * View store for the Client Instances grid — a control-plane view of every
 * client's deployed containers, grouped by client. Each client runs TWO
 * containers (role `api` + role `web`); the grid shows their live
 * status/image/digest/last-seen.
 *
 * Rule: route ViewStores are plain classes with `makeAutoObservable` and do NOT
 * extend ViewStore. The fleet list is read through the registered
 * `client-instances` ServiceStore (see serviceStores/infrastructure.stores.ts);
 * `instances` is a computed view over its entity cache. Optimistic per-row
 * status flips and the live sweep envelope are merged into that cache via the
 * ServiceStore (`addOrUpdateEvent`) so the MobX "no entities in a ViewStore"
 * rule holds; the authoritative re-pull that follows each action wins.
 *
 * Phase 5: per-row Start/Stop/Restart/Deploy/Rollback are gated on
 * {@link canManage} (operator+) and dispatched through the `instance-actions`
 * service (see ../instanceActions + ../InstanceActions dialogs). Nothing secret
 * is read or surfaced; instance rows carry only status/image/digest metadata,
 * never any host API key, and every mutating action goes to the API's
 * append-only audit.
 */
class ClientInstancesStore {
  rootStore: RootStore;

  loading = false;
  error: string | null = null;

  // Header filters
  searchQuery = "";
  statusFilter: ClientInstanceStatusFilter = "all";
  /** "all" or a specific clientId to scope the grid to one client. */
  clientFilter: string | "all" = "all";
  /** When true, only instances with an available update are shown. */
  updatesOnly = false;
  /** When true, discovery also surfaces in-scope unlabeled containers as
   *  'unassigned' (passes ?includeUnassigned=true to the API). */
  showUnassigned = false;

  // Column sort — applied WITHIN each client group (the grid renders one table
  // per client; the group-by-client structure itself never re-orders by column).
  // The default ("role" asc) is the canonical api→web role order.
  sortKey: ClientInstanceSortKey = "role";
  sortDir: SortDir = "asc";

  // ---- Assignment (Phase 4) ----
  assignTarget: ClientInstance | null = null;
  assignClientId = "";
  assignRole = "";
  assigning = false;
  assignError: string | null = null;

  // ---- Action state (Phase 5) ----
  /** instance ids with an in-flight start/stop/restart/deploy/rollback. */
  actioningIds: string[] = [];
  actionError: string | null = null;
  /** What the last per-client "Update all" did, in words (success only —
   *  a failure lands in actionError). */
  actionNotice: string | null = null;
  /** Client ids with an "Update all" in flight. */
  updatingClientIds: string[] = [];

  // ---- Live fleet-status feed (periodic reconcile sweep) ----
  /** Disposer for the `instance-status` subscription; null when unbound. */
  private liveDispose: (() => void) | null = null;
  private updateDispose: (() => void) | null = null;
  /** When the last sweep envelope was applied (for a freshness hint). */
  lastLiveUpdateAt: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  /** The registered `client-instances` ServiceStore. */
  private get instancesService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("client-instances") ?? this.rootStore.clientInstancesStore) as unknown as ServiceStoreType | null) ?? null
    );
  }

  /** The registered `instance-actions` ServiceStore (the mutating action seam). */
  private get actionsService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("instance-actions") ?? this.rootStore.instanceActionsStore) as unknown as ServiceStoreType | null) ?? null
    );
  }

  /** The global ClientStore (the full tenant list) — name resolution + assign picker. */
  private get globalClientStore():
    | {
        clients?: Array<{ id: string; name?: string; deletedAt?: string | null }>;
        fetchClients?: () => Promise<void>;
      }
    | undefined {
    return (this.rootStore as Record<string, unknown>).clientStore as
      | {
          clients?: Array<{ id: string; name?: string; deletedAt?: string | null }>;
          fetchClients?: () => Promise<void>;
        }
      | undefined;
  }

  /**
   * Fleet list. Per the MobX rule, entities live in the ServiceStore cache
   * (populated by `find`), not in this ViewStore — this is a computed view.
   */
  get instances(): ClientInstance[] {
    // Internal-designated containers are managed in the Internal Containers
    // view — this view is the CLIENT fleet, so they are excluded here (they
    // would otherwise collapse into the Unassigned group they just left).
    return (
      (this.instancesService?.entities ?? []) as unknown as ClientInstance[]
    ).filter((i) => i.assignmentState !== "internal");
  }

  // ---- RBAC ---------------------------------------------------------------

  /**
   * operator+ — whether the Start/Stop/Restart/Deploy/Rollback controls render.
   * The API is the real gate; this only governs visibility. See ../rbac.ts.
   */
  get canManage(): boolean {
    return canManageInfra(this.rootStore);
  }

  /** True while this row has its own action in flight OR its client is
   *  mid "Update all" — the batch redeploys every row, so none may be acted
   *  on separately until it finishes. */
  isActioning(instanceId: string): boolean {
    if (this.actioningIds.includes(instanceId)) return true;
    if (this.updatingClientIds.length === 0) return false;
    const clientId = this.instances.find((i) => i.id === instanceId)?.clientId;
    return !!clientId && this.updatingClientIds.includes(clientId);
  }

  clearActionError() {
    this.actionError = null;
  }

  clearActionNotice() {
    this.actionNotice = null;
  }

  isUpdatingClient(clientId: string): boolean {
    return this.updatingClientIds.includes(clientId);
  }

  /**
   * The rows "Update all" for this client would act on, judged by the cached
   * registry flag over the client's WHOLE fleet (not the filtered view — the
   * API batch updates every row of the client regardless of what is on
   * screen). Missing and action-blocked rows are excluded: the API skips the
   * first and refuses the second.
   */
  clientUpdates(clientId: string): ClientInstance[] {
    return this.instances.filter(
      (i) =>
        i.clientId === clientId &&
        i.updateAvailable === true &&
        i.enabled !== false &&
        !isClientInstanceMissing(i.status)
    );
  }

  // ---- Derived ------------------------------------------------------------

  /** Memoized id→name map over the global client list (O(1) lookups from the
   *  per-instance passes in matchesFilters/groupedByClient/clientOptions). */
  private get clientNameById(): Map<string, string> {
    const map = new Map<string, string>();
    for (const client of this.globalClientStore?.clients ?? []) {
      if (client.name) map.set(client.id, client.name);
    }
    return map;
  }

  /** Resolve a client's display name (denormalised on the row, else global store). */
  clientLabel(clientId: string | null, fallback?: string | null): string {
    if (fallback) return fallback;
    if (!clientId) return "Unassigned";
    return this.clientNameById.get(clientId) || clientId;
  }

  private matchesFilters(inst: ClientInstance): boolean {
    if (this.clientFilter !== "all" && inst.clientId !== this.clientFilter) {
      return false;
    }
    if (this.statusFilter === "running" && !isClientInstanceRunning(inst.status)) {
      return false;
    }
    if (this.statusFilter === "stopped" && isClientInstanceRunning(inst.status)) {
      return false;
    }
    if (this.updatesOnly && inst.updateAvailable !== true) {
      return false;
    }
    if (this.searchQuery.trim()) {
      const q = this.searchQuery.toLowerCase().trim();
      const haystack = [
        this.clientLabel(inst.clientId, inst.clientName),
        inst.containerName,
        inst.imageRef,
        inst.role,
        inst.dockerHostName,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  }

  get filteredInstances(): ClientInstance[] {
    return this.instances.filter((i) => this.matchesFilters(i));
  }

  /**
   * The filtered instances grouped by client, each group's instances ordered by
   * the active column sort (default: the canonical api→web role order). Groups
   * themselves are always sorted by client name — column sort applies WITHIN
   * each group only.
   */
  get groupedByClient(): ClientInstanceGroup[] {
    const byClient = new Map<string, ClientInstanceGroup>();
    for (const inst of this.filteredInstances) {
      // Unassigned containers (clientId null) collapse into one stable group.
      const groupId = inst.clientId ?? "unassigned";
      let group = byClient.get(groupId);
      if (!group) {
        group = {
          clientId: groupId,
          clientName: this.clientLabel(inst.clientId, inst.clientName),
          instances: [],
        };
        byClient.set(groupId, group);
      }
      group.instances.push(inst);
    }
    const roleRank = (role: string) => {
      const idx = CLIENT_INSTANCE_ROLE_ORDER.indexOf(role);
      return idx === -1 ? CLIENT_INSTANCE_ROLE_ORDER.length : idx;
    };
    const key = this.sortKey;
    const valueOf = (inst: ClientInstance): SortValue => {
      switch (key) {
        case "role":
          return roleRank(inst.role);
        case "status":
          return textValue(inst.status);
        case "image":
          return textValue(inst.imageRef);
        case "host":
          return textValue(inst.dockerHostName);
        case "container":
          return textValue(inst.containerName);
        case "lastSeen":
          return dateValue(inst.lastSeenAt);
      }
    };
    for (const group of byClient.values()) {
      group.instances = sortRows(group.instances, valueOf, this.sortDir);
    }
    return Array.from(byClient.values()).sort((a, b) =>
      a.clientName.localeCompare(b.clientName)
    );
  }

  /** Materialize an id→name map as name-sorted picker options. */
  private static toSortedOptions(
    seen: Map<string, string>
  ): Array<{ id: string; name: string }> {
    return Array.from(seen.entries())
      .map(([id, name]) => ({ id, name: String(name ?? id ?? "") }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  /** Distinct clients present in the (unfiltered) instance set, for the picker. */
  get clientOptions(): Array<{ id: string; name: string }> {
    const seen = new Map<string, string>();
    for (const inst of this.instances) {
      // Unassigned containers have clientId null — they are not a client.
      if (!inst.clientId) continue;
      if (!seen.has(inst.clientId)) {
        seen.set(inst.clientId, this.clientLabel(inst.clientId, inst.clientName));
      }
    }
    return ClientInstancesStore.toSortedOptions(seen);
  }

  /**
   * Client choices for the Assign dialog — the FULL tenant list from the
   * global ClientStore, so a client with no instances yet is still an
   * assignment target. Archived clients (deletedAt set) are excluded; any
   * instance-derived client missing from the global cache is merged in
   * defensively (stale cache / more tenants than one fetch page).
   */
  get assignClientOptions(): Array<{ id: string; name: string }> {
    const seen = new Map<string, string>();
    for (const client of this.globalClientStore?.clients ?? []) {
      if (client.deletedAt) continue;
      seen.set(client.id, client.name || client.id);
    }
    for (const opt of this.clientOptions) {
      if (!seen.has(opt.id)) seen.set(opt.id, opt.name);
    }
    return ClientInstancesStore.toSortedOptions(seen);
  }

  get totalCount(): number {
    return this.instances.length;
  }

  /** How many instances survive the header filters — what the grid actually
   *  renders. The summary cards deliberately count the WHOLE fleet, so the body
   *  shows a "Showing N of M" caption whenever these two disagree. */
  get filteredCount(): number {
    return this.filteredInstances.length;
  }

  /** True when a header filter or search is actually set — the only time the
   *  empty grid may say "No client instances match the current filters". */
  get hasActiveFilter(): boolean {
    return (
      this.searchQuery.trim().length > 0 ||
      this.statusFilter !== "all" ||
      this.clientFilter !== "all" ||
      this.updatesOnly
    );
  }

  /** True when the grid is showing less than the whole fleet. */
  get isFiltered(): boolean {
    return this.filteredCount !== this.totalCount;
  }

  get runningCount(): number {
    return this.instances.filter((i) => isClientInstanceRunning(i.status)).length;
  }

  get stoppedCount(): number {
    return this.instances.filter((i) => !isClientInstanceRunning(i.status)).length;
  }

  get clientCount(): number {
    return new Set(this.instances.map((i) => i.clientId).filter(Boolean)).size;
  }

  /** Instances with an available update (across the whole fleet, unfiltered). */
  get updatesAvailableCount(): number {
    return this.instances.filter((i) => i.updateAvailable === true).length;
  }

  /** Clients with at least one row "Update all" would update. */
  get clientsWithUpdatesCount(): number {
    return new Set(
      this.instances
        .filter((i) => i.clientId && this.clientUpdates(i.clientId).length > 0)
        .map((i) => i.clientId)
    ).size;
  }

  // ---- CSV export ---------------------------------------------------------

  /**
   * The fleet inventory as flat CSV records — exactly what is ON SCREEN:
   * `groupedByClient` already applies the header filters, the client-name group
   * order and the active column sort, so reading it here means the spreadsheet
   * row order matches the grid the operator was looking at.
   *
   * Field selection lives in ../infrastructureExports (which names every emitted
   * field explicitly, so no credential can reach the file); this getter only
   * decides which rows.
   */
  get exportRows(): ExportRecord[] {
    return this.groupedByClient.flatMap((group) =>
      group.instances.map((instance) =>
        toClientInstanceExportRow(instance, group.clientName)
      )
    );
  }

  get canExport(): boolean {
    return this.exportRows.length > 0;
  }

  /**
   * Download the on-screen fleet as CSV. Returns false rather than throwing on
   * an empty set — the toolkit's `exportToCsv` throws there, and the header
   * disables the button on {@link canExport}, so this is belt-and-braces.
   */
  exportCsv(): boolean {
    const rows = this.exportRows;
    if (rows.length === 0) return false;
    try {
      exportToCsv(rows, CLIENT_INSTANCE_EXPORT_COLUMNS, {
        fileName: `client-instances-${new Date().toISOString().slice(0, 10)}`,
      });
      return true;
    } catch (err) {
      console.error("Client instances CSV export failed:", err);
      return false;
    }
  }

  // ---- Filter setters -----------------------------------------------------

  setSearchQuery(q: string) {
    this.searchQuery = q;
  }

  setStatusFilter(filter: ClientInstanceStatusFilter) {
    this.statusFilter = filter;
  }

  setClientFilter(filter: string | "all") {
    this.clientFilter = filter;
  }

  setUpdatesOnly(value: boolean) {
    this.updatesOnly = value;
  }

  /** Column-header click: same column toggles direction, a new column starts
   *  in its natural direction (lastSeen newest-first, text A→Z). */
  setSort(key: ClientInstanceSortKey) {
    const next = nextSort(this.sortKey, this.sortDir, key, DESC_FIRST);
    this.sortKey = next.key;
    this.sortDir = next.dir;
  }

  /** Toggle discovery of unassigned containers and reload. */
  async setShowUnassigned(value: boolean) {
    this.showUnassigned = value;
    await this.loadInstances();
  }

  // ---- Loading ------------------------------------------------------------

  /** Fetch the global client list when the cache is empty (best-effort, async). */
  private ensureClientsLoaded() {
    const clientStore = this.globalClientStore;
    if (clientStore && !clientStore.clients?.length) {
      void clientStore.fetchClients?.();
    }
  }

  /** Entry point from the route. Loads instances + seeds the global client list. */
  async initialize() {
    // Seed the global client list so groups/the filter can resolve names even
    // when the API does not denormalise clientName onto each row.
    this.ensureClientsLoaded();
    // loadInstances swallows a sign-out: binding the live feed after it would
    // subscribe in the next person's session for a page they never opened.
    const ended = sessionMark(this.rootStore);
    await this.loadInstances();
    if (ended()) return;
    // Live updates: the API's reconcile sweep publishes the refreshed fleet on
    // a staff-only Feathers event every few minutes; apply it in place so a
    // crashed/vanished container shows without a manual refresh.
    void this.startLive();
  }

  /**
   * Bind the fleet-status feed (idempotent). Best-effort: when no socket
   * exists yet this stays unbound and the next initialize() retries — the grid
   * still works pull-only (refresh button / post-action reloads).
   */
  private async startLive() {
    if (this.liveDispose) return;
    const dispose = await subscribeInstanceStatus(this.rootStore, (envelope) =>
      this.applyStatusEnvelope(envelope)
    );
    if (dispose) {
      runInAction(() => {
        this.liveDispose = dispose;
      });
    }
    if (!this.updateDispose) {
      // Update-availability flips (registry sweep, false→true only): mark the
      // affected rows in place so a new badge appears without a reload. The
      // badge clears via the next status envelope / reload.
      const updateDispose = await subscribeInstanceUpdates(
        this.rootStore,
        (envelope) => this.applyUpdateEnvelope(envelope)
      );
      if (updateDispose) {
        runInAction(() => {
          this.updateDispose = updateDispose;
        });
      }
    }
  }

  /** Route exit teardown — drops the live feed listener. Idempotent. */
  dispose() {
    this.liveDispose?.();
    this.liveDispose = null;
    this.updateDispose?.();
    this.updateDispose = null;
  }

  /**
   * Apply one sweep envelope. The envelope carries the FULL refreshed fleet
   * (the same contract loadInstances pulls), so it simply replaces the list —
   * unless a manual load or an action round-trip is mid-flight, in which case
   * the authoritative re-pull that follows wins and the envelope is skipped.
   */
  private applyStatusEnvelope(envelope: InstanceStatusEnvelope) {
    if (this.loading || this.busy) return;
    const instances = envelope.instances;
    if (!Array.isArray(instances)) return;
    runInAction(() => {
      // Merge the refreshed fleet into the ServiceStore cache (idField `id`).
      const service = this.instancesService;
      for (const inst of instances) {
        service?.addOrUpdateEvent(inst as unknown as Record<string, unknown>);
      }
      this.lastLiveUpdateAt = envelope.at ?? new Date().toISOString();
    });
  }

  /**
   * Apply one update-availability envelope: flip the named cached rows to
   * update-available with their latest ref. Skipped mid-load/action for the
   * same reason as applyStatusEnvelope — the authoritative re-pull wins.
   */
  private applyUpdateEnvelope(envelope: InstanceUpdateEnvelope) {
    if (this.loading || this.busy) return;
    const flips = envelope.instances;
    if (!Array.isArray(flips) || flips.length === 0) return;
    runInAction(() => {
      const service = this.instancesService;
      for (const flip of flips) {
        const existing = this.instances.find((i) => i.id === flip.instanceId);
        if (!existing) continue;
        service?.addOrUpdateEvent({
          ...existing,
          updateAvailable: true,
          latestImageRef: flip.latestImageRef ?? existing.latestImageRef ?? null,
          updateAvailableSince: envelope.at ?? new Date().toISOString(),
        } as unknown as Record<string, unknown>);
      }
    });
  }

  async loadInstances() {
    this.loading = true;
    this.error = null;
    try {
      // ServiceStore.find populates its entity cache, which `instances` reads.
      //
      // NO `$limit` / `$sort` HERE, AND THAT IS DELIBERATE. `client-instances`
      // is not a table read: it is a live discovery proxy that walks every
      // Portainer connection/endpoint and returns a BARE ARRAY of every
      // container carrying the client-id label (see the API's
      // client-instances find()). It honours `$limit`/`$skip` (since 2026-09-23,
      // applied AFTER the full sweep, so a page is only a view) but this grid
      // wants the whole fleet, and a bare array carries no `total` — a capped
      // fetch here would make `totalCount` and the summary cards silently
      // short. `$sort` is still ignored; the API orders by client then role.
      //
      // Consequence: `instances` is the WHOLE discovered fleet, so `totalCount`
      // and the summary cards are honest with no caption needed. The grid's
      // "Showing N of M" caption is about the header FILTERS, not a fetch cap.
      const query: Record<string, unknown> = {};
      // Opt-in: also surface in-scope unlabeled containers as 'unassigned'.
      if (this.showUnassigned) query.includeUnassigned = true;
      // findOrThrow: the library ServiceStore resolves null on a failed find,
      // which rendered "No client instances match the current filters" with
      // tiles of 0 while discovery was down.
      await findOrThrow(this.instancesService, { query });
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.error = loadErrorMessage(err, "Failed to load client instances");
      });
      console.error("Failed to load client instances:", err);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /** Alias used by header refresh. */
  async reload() {
    await this.loadInstances();
  }

  // ---- Actions (Phase 5) --------------------------------------------------

  /** Any row or client action in flight — live envelopes wait for its re-pull. */
  private get busy(): boolean {
    return this.actioningIds.length > 0 || this.updatingClientIds.length > 0;
  }

  /**
   * "Update all" for one client (operator+): the API's batch update-to-latest,
   * which re-checks the registry for each row, updates api then web, skips a
   * row that turns out to be current, and stops at the first failure. The
   * page confirms before calling this — it redeploys production containers.
   */
  async updateClient(clientId: string, clientName: string): Promise<boolean> {
    if (!this.canManage || this.isUpdatingClient(clientId)) return false;
    this.updatingClientIds = [...this.updatingClientIds, clientId];
    this.actionError = null;
    this.actionNotice = null;
    try {
      const result = await runInstanceAction(this.actionsService, {
        action: "update-to-latest",
        clientId,
      });
      followHandOff(this.rootStore, result, null);
      const updated = Number(/updated (\d+)/.exec(result?.status ?? "")?.[1] ?? NaN);
      runInAction(() => {
        this.actionNotice =
          updated === 0
            ? `${clientName}: nothing to update — the registry has no newer image than what is running. The badge clears on the next check.`
            : Number.isFinite(updated)
              ? `${clientName}: updated ${updated} container${updated === 1 ? "" : "s"}.`
              : `${clientName}: update finished.`;
      });
      await this.loadInstances();
      return true;
    } catch (err: unknown) {
      // A sign-out ended the call: no error, and no follow-up read, which
      // would go out in the next person's session.
      if (isSessionEnded(err)) return false;
      runInAction(() => {
        this.actionError = `${clientName}: ${
          err instanceof Error ? err.message : "update failed"
        } — containers after the failing one were not touched.`;
      });
      await this.loadInstances();
      console.error("Update client to latest failed:", err);
      return false;
    } finally {
      runInAction(() => {
        this.updatingClientIds = this.updatingClientIds.filter((id) => id !== clientId);
      });
    }
  }

  private beginAction(instanceId: string) {
    if (!this.actioningIds.includes(instanceId)) {
      this.actioningIds = [...this.actioningIds, instanceId];
    }
    this.actionError = null;
  }

  private endAction(instanceId: string) {
    this.actioningIds = this.actioningIds.filter((id) => id !== instanceId);
  }

  /** Patch a single instance row in place (optimistic status updates). The merge
   *  goes through the ServiceStore cache (by idField `id`); the authoritative
   *  re-pull that follows each action supersedes it. No-op for an unknown id
   *  (only an already-cached row is patched, matching the prior map semantics). */
  private patchInstance(instanceId: string, patch: Partial<ClientInstance>) {
    const existing = this.instances.find((i) => i.id === instanceId);
    if (!existing) return;
    this.instancesService?.addOrUpdateEvent({
      ...existing,
      ...patch,
    } as unknown as Record<string, unknown>);
  }

  /**
   * The optimistic status to show while a lifecycle action is in flight. The
   * authoritative status comes from the post-action reload; this only smooths
   * the UI for the round-trip.
   */
  private optimisticStatusFor(
    action: InstanceActionType
  ): ClientInstanceStatus | null {
    switch (action) {
      case "start":
        return "starting";
      case "restart":
        return "restarting";
      case "stop":
        return "stopped";
      default:
        return null; // deploy/rollback: don't guess a container state.
    }
  }

  /**
   * Run a Start / Stop / Restart against one instance (operator+). Optimistically
   * flips the row's status, then re-pulls the grid for the authoritative state.
   * No secret is sent; the API proxies to Portainer with the host's encrypted key
   * and writes the audit row.
   */
  async runLifecycleAction(
    instance: ClientInstance,
    action: InstanceActionType,
    /** The confirm dialog's progress token (InstanceActionsStore.runConfirmed). */
    progressToken?: string
  ): Promise<boolean> {
    if (!this.canManage) return false;
    // Missing rows have no container to act on — the API refuses too; surface
    // the same message without burning the round-trip.
    if (isClientInstanceMissing(instance.status)) {
      this.actionError =
        "This instance is missing — its container no longer exists on the host. Refresh the list.";
      return false;
    }
    this.beginAction(instance.id);
    const optimistic = this.optimisticStatusFor(action);
    if (optimistic) this.patchInstance(instance.id, { status: optimistic });
    try {
      const result = await runInstanceAction(this.actionsService, {
        action,
        instanceId: instance.id,
        ...(progressToken ? { progressToken } : {}),
      });
      followHandOff(this.rootStore, result, instance);
      await this.loadInstances();
      return true;
    } catch (err: unknown) {
      // A sign-out ended the call: no error, and no follow-up read, which
      // would go out in the next person's session.
      if (isSessionEnded(err)) return false;
      runInAction(() => {
        this.actionError =
          err instanceof Error ? err.message : `Failed to ${action} instance`;
      });
      // Roll back the optimistic flip by re-pulling the truth.
      await this.loadInstances();
      console.error(`Instance ${action} failed:`, err);
      return false;
    } finally {
      runInAction(() => this.endAction(instance.id));
    }
  }

  /**
   * Remove a MISSING row from the cache (operator+). The container no longer
   * exists on its host; this soft-deletes the dead row (audited API-side) so
   * a client's out-of-band redeploy doesn't leave "Missing · Assigned"
   * duplicates sitting in the grid for the 7-day auto-purge. The API refuses
   * any non-missing row.
   */
  async removeInstance(instance: ClientInstance): Promise<boolean> {
    if (!this.canManage) return false;
    if (!isClientInstanceMissing(instance.status)) {
      this.actionError =
        "Only missing instances can be removed — stop or unassign a live one instead.";
      return false;
    }
    this.beginAction(instance.id);
    try {
      await runInstanceAction(this.actionsService, {
        action: "remove",
        instanceId: instance.id,
      });
      await this.loadInstances();
      return true;
    } catch (err: unknown) {
      // A sign-out ended the call: no error, and no follow-up read, which
      // would go out in the next person's session.
      if (isSessionEnded(err)) return false;
      runInAction(() => {
        this.actionError =
          err instanceof Error ? err.message : "Failed to remove instance";
      });
      await this.loadInstances();
      console.error("Instance remove failed:", err);
      return false;
    } finally {
      runInAction(() => this.endAction(instance.id));
    }
  }

  // ---- Assignment (Phase 4) -----------------------------------------------

  /** Open the assign dialog for an unassigned container. */
  startAssign(instance: ClientInstance) {
    // The picker needs the full tenant list; initialize() normally seeds it,
    // but re-check here so a cold cache still populates.
    this.ensureClientsLoaded();
    this.assignTarget = instance;
    this.assignClientId = "";
    this.assignRole = instance.role === "api" || instance.role === "web" ? instance.role : "";
    this.assignError = null;
  }

  clearAssign() {
    this.assignTarget = null;
    this.assignClientId = "";
    this.assignRole = "";
    this.assignError = null;
  }

  setAssignClientId(clientId: string) {
    this.assignClientId = clientId;
  }

  setAssignRole(role: string) {
    this.assignRole = role;
  }

  get isAssignValid(): boolean {
    return !!this.assignTarget && this.assignClientId.trim().length > 0;
  }

  /** Assign the target container to a client (operator+). */
  async submitAssign(): Promise<boolean> {
    if (!this.assignTarget || !this.canManage || !this.isAssignValid) return false;
    const instanceId = this.assignTarget.id;
    this.assigning = true;
    this.assignError = null;
    const ended = sessionMark(this.rootStore);
    try {
      await runInstanceAction(this.actionsService, {
        action: "assign",
        instanceId,
        clientId: this.assignClientId.trim(),
        role: this.assignRole || undefined,
      });
      await this.loadInstances();
      // Assigned; the reload swallows a sign-out, and the dialog is reset already.
      if (ended()) return true;
      runInAction(() => this.clearAssign());
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.assignError =
            err instanceof Error ? err.message : "Failed to assign instance";
        }
      });
      console.error("Assign failed:", err);
      return false;
    } finally {
      runInAction(() => {
        this.assigning = false;
      });
    }
  }

  /** Designate an unassigned container as IntraSync-internal infrastructure
   *  (operator+). It leaves this view and appears in Internal Containers. */
  async markInternal(instance: ClientInstance): Promise<boolean> {
    if (!this.canManage) return false;
    this.beginAction(instance.id);
    try {
      await runInstanceAction(this.actionsService, {
        action: "mark-internal",
        instanceId: instance.id,
      });
      await this.loadInstances();
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.actionError =
            err instanceof Error ? err.message : "Failed to mark container internal";
        }
      });
      console.error("Mark internal failed:", err);
      return false;
    } finally {
      runInAction(() => {
        this.endAction(instance.id);
      });
    }
  }

  /** Remove an override assignment (operator+). Only override-owned rows qualify. */
  async unassign(instance: ClientInstance): Promise<boolean> {
    if (!this.canManage) return false;
    this.beginAction(instance.id);
    try {
      await runInstanceAction(this.actionsService, {
        action: "unassign",
        instanceId: instance.id,
      });
      await this.loadInstances();
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.actionError =
            err instanceof Error ? err.message : "Failed to unassign instance";
        }
      });
      console.error("Unassign failed:", err);
      return false;
    } finally {
      runInAction(() => this.endAction(instance.id));
    }
  }

  /**
   * Deploy a specific image:tag to one instance (operator+). The API validates
   * image/tag, pulls, and redeploys; we re-pull for the resulting digest/status.
   */
  async deployInstance(
    instance: ClientInstance,
    image: string,
    tag: string
  ): Promise<boolean> {
    if (!this.canManage) return false;
    this.beginAction(instance.id);
    try {
      const result = await runInstanceAction(this.actionsService, {
        action: "deploy",
        instanceId: instance.id,
        image,
        tag,
      });
      followHandOff(this.rootStore, result, instance);
      await this.loadInstances();
      return true;
    } catch (err: unknown) {
      // A sign-out ended the call: no error, and no follow-up read, which
      // would go out in the next person's session.
      if (isSessionEnded(err)) return false;
      runInAction(() => {
        this.actionError =
          err instanceof Error ? err.message : "Failed to deploy instance";
      });
      await this.loadInstances();
      console.error("Instance deploy failed:", err);
      return false;
    } finally {
      runInAction(() => this.endAction(instance.id));
    }
  }

  /**
   * Toggle the operator action gate (client_instances.enabled) for one
   * instance (operator+). Dispatched through `instance-actions` as the audited
   * 'enable' / 'disable' verbs; the row is optimistically flipped and the grid
   * re-pulled for the authoritative state.
   */
  async setInstanceEnabled(
    instance: ClientInstance,
    enabled: boolean
  ): Promise<boolean> {
    if (!this.canManage) return false;
    this.beginAction(instance.id);
    this.patchInstance(instance.id, { enabled });
    try {
      await runInstanceAction(this.actionsService, {
        action: enabled ? "enable" : "disable",
        instanceId: instance.id,
      });
      await this.loadInstances();
      return true;
    } catch (err: unknown) {
      // A sign-out ended the call: no error, and no follow-up read, which
      // would go out in the next person's session.
      if (isSessionEnded(err)) return false;
      runInAction(() => {
        this.actionError =
          err instanceof Error
            ? err.message
            : `Failed to ${enabled ? "enable" : "disable"} instance`;
      });
      await this.loadInstances();
      console.error("Instance enable/disable failed:", err);
      return false;
    } finally {
      runInAction(() => this.endAction(instance.id));
    }
  }

  /** Roll one instance back to a prior image digest (operator+). */
  async rollbackInstance(
    instance: ClientInstance,
    toDigest: string
  ): Promise<boolean> {
    if (!this.canManage) return false;
    this.beginAction(instance.id);
    try {
      const result = await runInstanceAction(this.actionsService, {
        action: "rollback",
        instanceId: instance.id,
        toDigest,
      });
      followHandOff(this.rootStore, result, instance);
      await this.loadInstances();
      return true;
    } catch (err: unknown) {
      // A sign-out ended the call: no error, and no follow-up read, which
      // would go out in the next person's session.
      if (isSessionEnded(err)) return false;
      runInAction(() => {
        this.actionError =
          err instanceof Error ? err.message : "Failed to roll back instance";
      });
      await this.loadInstances();
      console.error("Instance rollback failed:", err);
      return false;
    } finally {
      runInAction(() => this.endAction(instance.id));
    }
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.dispose();
    this.loading = false;
    this.error = null;
    this.searchQuery = "";
    this.statusFilter = "all";
    this.clientFilter = "all";
    this.updatesOnly = false;
    this.showUnassigned = false;
    this.sortKey = "role";
    this.sortDir = "asc";
    this.assignTarget = null;
    this.assignClientId = "";
    this.assignRole = "";
    this.assigning = false;
    this.assignError = null;
    this.actioningIds = [];
    this.actionError = null;
    this.actionNotice = null;
    this.updatingClientIds = [];
    this.liveDispose = null;
    this.updateDispose = null;
    this.lastLiveUpdateAt = null;
  }
}

export default ClientInstancesStore;
